import { Skeleton } from '@up2stay/up2promo-ui';
import type { ComponentType, ReactNode } from 'react';

import type { FeatureKey } from '@/core/authz/entitlement-catalog';
import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import { PageHeader } from '@/core/components/page-header';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { resolveDictKey } from '@/core/lib/resolve-dict-key';
import { useDictionary } from '@/core/providers/dictionary-provider';

interface EntitlementGateBodyProps {
  title: string;
  children: ReactNode;
}

function EntitlementGateBody({ title, children }: EntitlementGateBodyProps) {
  return (
    <AppPageContent>
      <AppPageLayout className="overflow-x-hidden min-w-0">
        <PageHeader title={title} />
        <div className="flex flex-1 flex-col gap-4">{children}</div>
      </AppPageLayout>
    </AppPageContent>
  );
}

export interface WithRequiredEntitlementOptions {
  feature: FeatureKey;
  /** i18n dot-path for PageHeader title while gated or loading */
  pageTitleKey: string;
  /**
   * @deprecated Gate chrome is title-only; UpgradeCard carries the feature
   * teaser. Kept optional for call-site compat.
   */
  pageDescriptionKey?: string;
}

/**
 * Gates workspace routes by plan entitlement.
 * Compose inside `withRequiredPermissions` so permission-denied redirects first.
 *
 * Owns a stable `AppIonPage` so loading/denied → entitled never remounts Ionic's
 * page root (blank-shell / `ion-page-invisible` race).
 */
export function withRequiredEntitlement<P extends object>(
  Component: ComponentType<P>,
  options: WithRequiredEntitlementOptions,
) {
  const { feature, pageTitleKey } = options;

  return function ProtectedByEntitlementComponent(props: P) {
    const { isFeatureEnabled, isLoading } = useEntitlements();
    const dict = useDictionary();
    const upgradeDict = dict.entitlements.upgrade;

    const title =
      resolveDictKey(dict as Record<string, unknown>, pageTitleKey) ??
      pageTitleKey;

    let body: ReactNode;
    if (isLoading) {
      body = (
        <EntitlementGateBody title={title}>
          <div className="space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-16 w-full rounded-lg" />
          </div>
          <p className="text-sm text-muted-foreground">
            {upgradeDict.checkingEntitlements}
          </p>
        </EntitlementGateBody>
      );
    } else if (!isFeatureEnabled(feature)) {
      body = (
        <EntitlementGateBody title={title}>
          <UpgradeCard feature={feature} compact={false} />
        </EntitlementGateBody>
      );
    } else {
      body = <Component {...props} />;
    }

    return <AppIonPage>{body}</AppIonPage>;
  };
}
